import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyPurgeConfirmDialog from '../components/CompanyPurgeConfirmDialog';
import { searchCompaniesForPurge, purgeCompany } from '../api/companyProfileApi';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import PaginationBar from '../../../shared/components/pagination/PaginationBar';
import { PageSkeleton } from '../../../components/loading/skeletons';
import './AdminPurgeCompanyPage.css';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
};

export default function AdminPurgeCompanyPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listMode, setListMode] = useState('list');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const loadCompanies = useCallback(async ({ query = '', nextPage = 0, nextPageSize = DEFAULT_PAGE_SIZE } = {}) => {
    setLoading(true);
    setError('');

    try {
      const data = await searchCompaniesForPurge({
        query,
        page: nextPage,
        pageSize: nextPageSize,
      });
      const nextCompanies = Array.isArray(data?.companies) ? data.companies : [];
      setCompanies(nextCompanies);
      setTotal(Number.isFinite(data?.total) ? data.total : nextCompanies.length);
      setPage(Number.isFinite(data?.page) ? data.page : nextPage);
      setPageSize(Number.isFinite(data?.pageSize) ? data.pageSize : nextPageSize);
      setListMode(data?.mode === 'search' ? 'search' : 'list');
      setActiveQuery(query);

      if (query && nextCompanies.length === 0) {
        setError('No companies found. Try company name, business email, owner email, or portal URL.');
      }
    } catch (err) {
      console.error(err);
      setCompanies([]);
      setTotal(0);
      setError(err?.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies({ query: '', nextPage: 0, nextPageSize: pageSize });
  }, [loadCompanies]);

  const closePurgeDialog = () => {
    if (purgeBusy) {
      return;
    }
    setPurgeTarget(null);
    setConfirmName('');
    setPurgeError('');
  };

  const handleSearch = async () => {
    const query = searchInput.trim();
    if (query.length > 0 && query.length < 2) {
      setError('Enter at least 2 characters to search.');
      return;
    }

    closePurgeDialog();
    setPage(0);
    await loadCompanies({ query, nextPage: 0, nextPageSize: pageSize });
  };

  const handleClearSearch = async () => {
    setSearchInput('');
    closePurgeDialog();
    setPage(0);
    await loadCompanies({ query: '', nextPage: 0, nextPageSize: pageSize });
  };

  const handleSelectCompany = (company) => {
    setPurgeTarget({
      id: company.id,
      name: company.name,
      email: company.email,
      ownerId: company.ownerId,
    });
    setConfirmName('');
    setPurgeError('');
    setError('');
  };

  const handleConfirmPurge = async () => {
    if (!purgeTarget?.id) {
      return;
    }

    const companyName = purgeTarget.name || '';
    if (confirmName.trim() !== companyName.trim()) {
      return;
    }

    setPurgeBusy(true);
    setPurgeError('');

    try {
      await purgeCompany({
        companyId: purgeTarget.id,
        confirmCompanyName: confirmName.trim(),
      });
      closePurgeDialog();
      setSearchInput('');
      setActiveQuery('');
      setPage(0);
      await loadCompanies({ query: '', nextPage: 0, nextPageSize: pageSize });
    } catch (err) {
      console.error('Failed to purge company:', err);
      setPurgeError(err?.message || 'Failed to purge company.');
    } finally {
      setPurgeBusy(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const handlePageChange = async (nextPage) => {
    closePurgeDialog();
    setPage(nextPage);
    await loadCompanies({ query: activeQuery, nextPage, nextPageSize: pageSize });
  };

  const handlePageSizeChange = async (event) => {
    const nextPageSize = Number(event.target.value) || DEFAULT_PAGE_SIZE;
    closePurgeDialog();
    setPage(0);
    setPageSize(nextPageSize);
    await loadCompanies({ query: activeQuery, nextPage: 0, nextPageSize });
  };

  const resultsTitle = listMode === 'search'
    ? `${total} search result${total === 1 ? '' : 's'}`
    : `${total} compan${total === 1 ? 'y' : 'ies'}`;

  const emptyMessage = listMode === 'search'
    ? 'No companies matched your search.'
    : 'No companies found.';

  return (
    <div className="admin-page admin-purge-company-page">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Purge Company</h1>
          <p className="admin-page__subtitle">
            Super Admin testing tool. Browse all companies or search, then permanently delete one.
          </p>
        </div>
        <button type="button" className="at-btn" onClick={() => navigate(adminPath(ADMIN_PATHS.TOOLS))}>
          Back to Admin Tools
        </button>
      </div>

      <div className="admin-card admin-purge-company-page__lookup">
        <label className="admin-purge-company-page__label" htmlFor="purge-company-search">
          Search companies
        </label>
        <div className="admin-purge-company-page__lookup-row">
          <input
            id="purge-company-search"
            className="admin-purge-company-page__input"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Filter by name, email, owner, or portal URL"
            disabled={loading}
            autoComplete="off"
          />
          <button
            type="button"
            className="at-btn"
            onClick={handleSearch}
            disabled={loading || (searchInput.trim().length > 0 && searchInput.trim().length < 2)}
          >
            {loading ? 'Loading…' : 'Search'}
          </button>
          {searchInput.trim() ? (
            <button
              type="button"
              className="at-btn"
              onClick={handleClearSearch}
              disabled={loading}
            >
              Clear
            </button>
          ) : null}
        </div>
        <p className="admin-purge-company-page__hint">
          All companies load in the list below. Search matches name, email, owner, portal URL, or company ID.
        </p>
        {error ? (
          <div className="admin-purge-company-page__alert admin-purge-company-page__alert--error" role="alert">
            {error}
          </div>
        ) : null}
      </div>

      <div className="admin-card admin-purge-company-page__results">
        <div className="admin-purge-company-page__results-header">
          <h2 className="admin-purge-company-page__results-title">{resultsTitle}</h2>
          <label className="admin-purge-company-page__page-size">
            Rows per page
            <select
              className="admin-purge-company-page__page-size-select"
              value={pageSize}
              onChange={handlePageSizeChange}
              disabled={loading}
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <PageSkeleton showHeader={false} variant="table" rows={6} label="Loading companies" />
        ) : companies.length === 0 ? (
          <div className="admin-purge-company-page__empty">{emptyMessage}</div>
        ) : (
          <div className="admin-purge-company-page__results-list">
            {companies.map((company) => {
              const isSelected = purgeTarget?.id === company.id;
              return (
                <button
                  key={company.id}
                  type="button"
                  className={`admin-purge-company-page__result${isSelected ? ' admin-purge-company-page__result--selected' : ''}`}
                  onClick={() => handleSelectCompany(company)}
                >
                  <div className="admin-purge-company-page__result-main">
                    <div className="admin-purge-company-page__result-name">{company.name || 'Company'}</div>
                    <div className="admin-purge-company-page__result-meta">
                      {company.email || 'No business email'}
                      {company.ownerName ? ` · Owner: ${company.ownerName}` : ''}
                      {company.ownerEmail ? ` (${company.ownerEmail})` : ''}
                    </div>
                    <div className="admin-purge-company-page__result-meta">
                      {company.portalSlug ? `Portal: ${company.portalSlug}` : 'No portal URL'}
                      {` · Created ${formatDate(company.createdAt)}`}
                    </div>
                  </div>
                  <span className="admin-purge-company-page__result-action">
                    {isSelected ? 'Selected' : 'Select'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {!loading && total > 0 ? (
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            className="admin-purge-company-page__pagination"
            ariaLabel="Companies pagination"
          />
        ) : null}
      </div>

      <CompanyPurgeConfirmDialog
        open={Boolean(purgeTarget)}
        company={purgeTarget}
        busy={purgeBusy}
        confirmName={confirmName}
        onConfirmNameChange={setConfirmName}
        onCancel={closePurgeDialog}
        onConfirm={handleConfirmPurge}
        error={purgeError}
      />
    </div>
  );
}
